import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import type { Resource } from "@/types/resource";
import { ResourceItem } from "@/components/ui/ResourceItem";
import { cn } from "@/lib/utils";

type ResourceShelfHeadingProps = {
  title: string;
  subtitle: string;
  moreLink?: { to: string; label: string };
  variant?: "teal" | "dark";
};

function useMobileResourceLayout() {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return narrow;
}

const ShelfHeading = ({ title, subtitle, moreLink, variant = "teal" }: ResourceShelfHeadingProps) => {
  const isTeal = variant === "teal";

  return (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 px-2 md:px-0">
      <div className="flex flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2
            className={cn(
              "font-literata text-base font-semibold md:text-xl",
              isTeal ? "text-white" : "text-stone-900"
            )}
          >
            {title}
          </h2>
          <p
            className={cn(
              "font-inter text-xs italic md:text-sm",
              isTeal ? "text-white/75" : "text-stone-400"
            )}
          >
            {subtitle}
          </p>
        </div>
      </div>
      {moreLink && (
        <Link
          to={moreLink.to}
          className={cn(
            "inline-flex items-center gap-1 font-inter text-xs font-medium transition md:text-sm",
            isTeal ? "text-white/80 hover:text-white" : "text-bodhi-teal hover:text-bodhi-teal/80"
          )}
        >
          <span>{moreLink.label}</span>
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      )}
    </div>
  );
};

export const HorizontalResourceShelf = ({
  resources,
  favoriteIds = [],
  onFavoriteClick,
  className,
}: {
  resources: Resource[];
  favoriteIds?: string[];
  onFavoriteClick: (id: string) => void;
  className?: string;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const resourceKey = resources.map((r) => r.id).join(",");

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const canScroll = scrollWidth > clientWidth + 4;
      setShowLeftArrow(scrollLeft > 2);
      setShowRightArrow(canScroll ? scrollLeft < scrollWidth - clientWidth - 2 : false);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const handleResize = () => checkScrollButtons();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resourceKey]);

  const scrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -340, behavior: "smooth" });
    setTimeout(checkScrollButtons, 320);
  };

  const scrollNext = () => {
    scrollRef.current?.scrollBy({ left: 340, behavior: "smooth" });
    setTimeout(checkScrollButtons, 320);
  };

  return (
    <div className={cn("relative -mx-8", className)}>
      {showLeftArrow && (
        <button
          type="button"
          onClick={scrollPrev}
          className="absolute left-2 top-1/2 z-20 flex -translate-y-1/2 rounded-full border border-gray-200 bg-white/95 p-2 shadow-lg transition hover:bg-white"
          aria-label="Scroll resources left"
        >
          <ChevronLeft className="h-5 w-5 text-gray-700" />
        </button>
      )}
      {showRightArrow && (
        <button
          type="button"
          onClick={scrollNext}
          className="absolute right-2 top-1/2 z-20 flex -translate-y-1/2 rounded-full border border-gray-200 bg-white/95 p-2 shadow-lg transition hover:bg-white"
          aria-label="Scroll resources right"
        >
          <ChevronRight className="h-5 w-5 text-gray-700" />
        </button>
      )}
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide pb-4 px-8"
        onScroll={checkScrollButtons}
      >
        <div className="flex gap-6">
          {resources.map((resource, index) => (
            <div key={resource.id ?? index} className="h-[25.2rem] w-72 shrink-0">
              <ResourceItem
                id={resource.id}
                type={resource.type}
                title={resource.title}
                description={resource.description}
                meta={resource.meta}
                addedDate={resource.addedDate}
                photo={resource.photo}
                url={resource.url}
                storagePath={resource.storagePath}
                isFavorited={favoriteIds.includes(resource.id)}
                onFavoriteClick={onFavoriteClick}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MobileResourceShelf = ({
  resources,
  favoriteIds = [],
  onFavoriteClick,
  className,
}: {
  resources: Resource[];
  favoriteIds?: string[];
  onFavoriteClick: (id: string) => void;
  className?: string;
}) => (
  <div className={cn("mt-4 grid grid-cols-1 gap-4 px-2 sm:grid-cols-2 md:gap-5", className)}>
    {resources.map((resource, index) => (
      <div key={resource.id ?? index}>
        <ResourceItem
          id={resource.id}
          type={resource.type}
          title={resource.title}
          description={resource.description}
          meta={resource.meta}
          addedDate={resource.addedDate}
          photo={resource.photo}
          url={resource.url}
          storagePath={resource.storagePath}
          isFavorited={favoriteIds.includes(resource.id)}
          onFavoriteClick={onFavoriteClick}
        />
      </div>
    ))}
  </div>
);

/** Carousel on desktop, stacked grid on narrow viewports. */
export const ResourceScrollOrGrid = ({
  resources,
  favoriteIds,
  onFavoriteClick,
  classNameDesktop,
  classNameMobile,
}: {
  resources: Resource[];
  favoriteIds?: string[];
  onFavoriteClick: (id: string) => void;
  classNameDesktop?: string;
  classNameMobile?: string;
}) => {
  const narrow = useMobileResourceLayout();
  if (narrow) {
    return (
      <MobileResourceShelf
        resources={resources}
        favoriteIds={favoriteIds}
        onFavoriteClick={onFavoriteClick}
        className={classNameMobile}
      />
    );
  }
  return (
    <HorizontalResourceShelf
      resources={resources}
      favoriteIds={favoriteIds}
      onFavoriteClick={onFavoriteClick}
      className={classNameDesktop}
    />
  );
};

/** Full section wrapper: heading row + carousel / grid tuned for teal background behind library pages. */
export const ResourceShelfSection = ({
  title,
  subtitle,
  moreLink,
  resources,
  favoriteIds,
  onFavoriteClick,
}: ResourceShelfHeadingProps & {
  resources: Resource[];
  favoriteIds: string[];
  onFavoriteClick: (id: string) => void;
}) => {
  return (
    <section>
      <ShelfHeading title={title} subtitle={subtitle} moreLink={moreLink} variant="teal" />
      <ResourceScrollOrGrid
        resources={resources}
        favoriteIds={favoriteIds}
        onFavoriteClick={onFavoriteClick}
        classNameDesktop="mt-6"
      />
    </section>
  );
};
